import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { AmbientBackdrop } from "./components/cabin/AmbientBackdrop";
import { ControlDrawer } from "./components/cabin/ControlDrawer";
import { DesktopStage } from "./components/cabin/DesktopStage";
import { applyPreset } from "./components/cabin/ScriptControls";
import { SettingsModal } from "./components/cabin/SettingsModal";
import {
  AUDIO_COMPANION_ANSWER,
  AUDIO_COMPANION_GENERATION_REPLY,
  AUDIO_COMPANION_SIMILAR_REPLY,
  createAudioCompanionVoiceFeedback,
  createInitialAudioCompanionState,
  matchAudioCompanionVoiceIntent,
  type AudioCompanionDemoState,
  type AudioCompanionProgramId
} from "./config/audioCompanionDemo";
import { createCompanionVoiceFeedback } from "./config/companionVoice";
import { getCompanionFeedbackAudio, getVideoActorFeedbackAudio } from "./config/companionFeedbackAudio";
import {
  createInitialVideoCompanionState,
  getVideoCompanionVideo,
  matchVideoCompanionVoiceIntent,
  type VideoCompanionDemoState,
  type VideoCompanionVideoId
} from "./config/videoCompanionDemo";
import type { ChargingStation } from "./config/amapChargingDemo";
import type { CarwashShop } from "./config/amapCarwashDemo";
import type { FuelStation } from "./config/amapFuelDemo";
import {
  activateCarwashMode,
  createCarwashModePrompt,
  idleCarwashMode,
  type CarwashModeState
} from "./config/carwashMode";
import {
  createChargingSession,
  idleChargingSession,
  updateChargingSessionBatteryLevel,
  type ChargingSessionState
} from "./config/chargingSession";
import { cockpitTheme, themeToCssVars } from "./config/theme";
import { initialCabinState } from "./config/demoPresets";
import {
  createParcelExitReminder,
  idleParcelExitReminder,
  type ParcelExitReminderState
} from "./config/parcelExitReminder";
import {
  authorizeRefuel,
  createRefuelAuthorizationPrompt,
  idleRefuelAuthorization,
  type RefuelAuthorizationState
} from "./config/refuelAuthorization";
import { withDerivedRange } from "./config/vehicleEnergy";
import {
  CockpitDataProvider,
  applyCockpitDataToCabinState,
  useCockpitDataModel
} from "./context/CockpitDataContext";
import { useRealtimeVoiceGateway } from "./hooks/useRealtimeVoiceGateway";
import { useSpeechSynthesis } from "./hooks/useSpeechSynthesis";
import { computeDecisionSummary } from "./logic/decisionEngine";
import { defaultModelSettings, loadModelSettings, requestDecisionCopy, requestSemanticIntent } from "./logic/modelClient";
import {
  applySemanticInterpretation,
  interpretVoiceCommand,
  type VoiceCommandContext,
  type VoiceCommandIntent,
  type VoiceDestinationSearchRequest
} from "./logic/voiceCommand";
import type { CabinState, DemoPreset, ModelSettings, ScenarioDecision, SemanticIntentResponse, ServiceId } from "./types/domain";
import { assetUrl } from "./utils/assetUrl";

export function App() {
  const [state, setState] = useState<CabinState>(() => withDerivedRange(initialCabinState));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [parkingPaymentOpen, setParkingPaymentOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceId | null>(null);
  const [confirmedDecision, setConfirmedDecision] = useState<ScenarioDecision | null>(null);
  const [chargingSession, setChargingSession] = useState<ChargingSessionState>(idleChargingSession);
  const [carwashMode, setCarwashMode] = useState<CarwashModeState>(idleCarwashMode);
  const [refuelAuthorization, setRefuelAuthorization] =
    useState<RefuelAuthorizationState>(idleRefuelAuthorization);
  const [parcelExitReminder, setParcelExitReminder] =
    useState<ParcelExitReminderState>(idleParcelExitReminder);
  const [modelSettings, setModelSettings] = useState<ModelSettings>(defaultModelSettings);
  const [copyOverride, setCopyOverride] = useState<{ text: string; source: "local" | "model" | "fallback" } | null>(null);
  const [voiceFeedback, setVoiceFeedback] = useState<VoiceCommandIntent | null>(null);
  const [manualVoiceCaption, setManualVoiceCaption] = useState({
    submitted: false,
    text: ""
  });
  const [voiceDestinationSearchRequest, setVoiceDestinationSearchRequest] =
    useState<VoiceDestinationSearchRequest | null>(null);
  const [accidentGuideOpen, setAccidentGuideOpen] = useState(false);
  const [accidentCallVisible, setAccidentCallVisible] = useState(true);
  const [audioCompanionDemo, setAudioCompanionDemo] =
    useState<AudioCompanionDemoState | null>(null);
  const [videoCompanionDemo, setVideoCompanionDemo] =
    useState<VideoCompanionDemoState | null>(null);
  const voiceOutput = useSpeechSynthesis({ defaultEnabled: true, lang: "zh-CN" });

  const cockpitData = useCockpitDataModel(state);
  const decisionState = useMemo(
    () => applyCockpitDataToCabinState(state, cockpitData),
    [cockpitData, state]
  );
  const baseSummary = useMemo(() => computeDecisionSummary(decisionState), [decisionState]);
  const summary = useMemo(
    () => ({
      ...baseSummary,
      generatedCopy: copyOverride?.text ?? baseSummary.generatedCopy,
      copySource: copyOverride?.source ?? baseSummary.copySource
    }),
    [baseSummary, copyOverride]
  );
  const realtimeVoice = useRealtimeVoiceGateway({
    getContext: () => ({
      activeServiceId: summary.activeDecision?.scenario.id ?? null,
      topServiceId: summary.topDecision.score > 0 ? summary.topDecision.scenario.id : null,
      state: decisionState
    }),
    onFallbackTranscript: (transcript) => {
      void handleVoiceTranscript(transcript);
    },
    onFinalTranscript: handleCompanionTranscript,
    onFinalIntent: ({ transcript, semantic }) => {
      void handleVoiceTranscript(transcript, semantic);
    }
  });
  const manualVoiceCaptionText = manualVoiceCaption.text.trim();
  const voiceCaptionText = manualVoiceCaption.submitted
    ? realtimeVoice.transcript || manualVoiceCaptionText
    : manualVoiceCaptionText || realtimeVoice.transcript;
  const voiceCaptionActive =
    Boolean(manualVoiceCaptionText) ||
    realtimeVoice.audioListening ||
    realtimeVoice.phase === "connecting" ||
    realtimeVoice.phase === "asr" ||
    realtimeVoice.phase === "understanding" ||
    realtimeVoice.phase === "speaking";

  const selectedDecision = selectedService
    ? summary.decisions.find((decision) => decision.scenario.id === selectedService) ?? null
    : null;

  useEffect(() => {
    loadModelSettings().then(setModelSettings);
  }, []);

  const handleManualVoiceCaptionChange = useCallback((text: string) => {
    setManualVoiceCaption({
      submitted: false,
      text
    });
  }, []);

  const handleManualVoiceCaptionSubmit = useCallback((text: string) => {
    setManualVoiceCaption({
      submitted: true,
      text
    });
  }, []);

  useEffect(() => {
    if (!manualVoiceCaption.submitted || !manualVoiceCaption.text.trim()) {
      return undefined;
    }

    const shouldReleaseManualCaption =
      Boolean(realtimeVoice.transcript.trim()) ||
      realtimeVoice.phase === "done" ||
      realtimeVoice.phase === "error" ||
      realtimeVoice.phase === "idle";

    if (!shouldReleaseManualCaption) {
      return undefined;
    }

    const clearTimer = window.setTimeout(() => {
      setManualVoiceCaption((current) => current.submitted
        ? {
            submitted: false,
            text: ""
          }
        : current
      );
    }, 800);

    return () => window.clearTimeout(clearTimer);
  }, [manualVoiceCaption, realtimeVoice.phase, realtimeVoice.transcript]);

  useEffect(() => {
    if (chargingSession.stage !== "chargingStarted") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setChargingSession((current) => current.stage === "chargingStarted"
        ? {
            ...current,
            stage: "restRecommend"
          }
        : current
      );
    }, 1600);

    return () => window.clearTimeout(timeoutId);
  }, [chargingSession.stage]);

  useEffect(() => {
    setChargingSession((current) => updateChargingSessionBatteryLevel({
      batteryLevel: decisionState.batteryLevel,
      session: current
    }));
  }, [decisionState.batteryLevel]);

  useEffect(() => {
    let cancelled = false;
    setCopyOverride(null);
    requestDecisionCopy(decisionState, baseSummary.activeDecision, modelSettings).then((response) => {
      if (!cancelled) {
        setCopyOverride({ text: response.text, source: response.source });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [decisionState, baseSummary.activeDecision, modelSettings]);

  useEffect(() => {
    function handleKey(event: KeyboardEvent) {
      if (event.defaultPrevented || event.isComposing || isTextEntryTarget(event.target)) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "k") {
        setSettingsOpen(true);
      }
      if (event.key.toLowerCase() === "h") {
        toggleDesktop();
      }
    }

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  function patchState(patch: Partial<CabinState>) {
    setState((current) => withDerivedRange({
      ...current,
      ...patch,
      scriptTrigger: patch.scriptTrigger ?? "none"
    }));
  }

  function toggleDesktop() {
    setState((current) => withDerivedRange({
      ...current,
      desktop: current.desktop === "standard" ? "ai" : "standard",
      scriptTrigger: "none"
    }));
  }

  function handleConfirm() {
    if (selectedDecision) {
      if (selectedDecision.scenario.id === "parking") {
        setConfirmedDecision(null);
        setParkingPaymentOpen(true);
        return;
      }

      if (selectedDecision.scenario.id === "collision") {
        setAccidentGuideOpen(true);
        setAccidentCallVisible(true);
        setConfirmedDecision(null);
        setSelectedService(null);
        setParkingPaymentOpen(false);
        setChargingSession(idleChargingSession);
        setCarwashMode(idleCarwashMode);
        setRefuelAuthorization(idleRefuelAuthorization);
        setParcelExitReminder(idleParcelExitReminder);
        return;
      }

      const showSuccessToast = selectedDecision.scenario.id !== "parcel" && selectedDecision.scenario.id !== "refuel";
      setConfirmedDecision(showSuccessToast ? selectedDecision : null);
      setSelectedService(null);
      if (selectedDecision.scenario.id === "parcel") {
        setRefuelAuthorization(idleRefuelAuthorization);
        setState((current) => withDerivedRange({
          ...current,
          desktop: "standard",
          parcelPending: true,
          parcelOnRoute: true
        }));
      }
      if (selectedDecision.scenario.id === "carwash") {
        setCarwashMode(idleCarwashMode);
        setRefuelAuthorization(idleRefuelAuthorization);
        setState((current) => withDerivedRange({
          ...current,
          desktop: "standard",
          carwashCoupon: true
        }));
      }
      if (selectedDecision.scenario.id === "charging") {
        setChargingSession(idleChargingSession);
        setRefuelAuthorization(idleRefuelAuthorization);
        setState((current) => withDerivedRange({
          ...current,
          desktop: "standard",
          chargerAvailability: Math.max(current.chargerAvailability, 6),
          chargerPrice: 1.2
        }));
      }
      if (selectedDecision.scenario.id === "refuel") {
        setRefuelAuthorization(idleRefuelAuthorization);
        setState((current) => withDerivedRange({
          ...current,
          desktop: "standard",
          fuelLevel: Math.min(current.fuelLevel, 28),
          fuelRangeKm: Math.min(current.fuelRangeKm, 220)
        }));
      }
      if (showSuccessToast) {
        setTimeout(() => setConfirmedDecision(null), 4200);
      }
    }
  }

  function handleReset() {
    setState(withDerivedRange(initialCabinState));
    setSelectedService(null);
    setConfirmedDecision(null);
    setChargingSession(idleChargingSession);
    setCarwashMode(idleCarwashMode);
    setRefuelAuthorization(idleRefuelAuthorization);
    setParcelExitReminder(idleParcelExitReminder);
    setParkingPaymentOpen(false);
    setCopyOverride(null);
    setVoiceFeedback(null);
    setManualVoiceCaption({
      submitted: false,
      text: ""
    });
    setVoiceDestinationSearchRequest(null);
    setAccidentGuideOpen(false);
    setAccidentCallVisible(true);
    setAudioCompanionDemo(null);
    setVideoCompanionDemo(null);
  }

  function handlePreset(preset: DemoPreset) {
    setState((current) => withDerivedRange(applyPreset(current, preset)));
    setSelectedService(null);
    setConfirmedDecision(null);
    setChargingSession(idleChargingSession);
    setCarwashMode(idleCarwashMode);
    setRefuelAuthorization(idleRefuelAuthorization);
    setParcelExitReminder(idleParcelExitReminder);
    setParkingPaymentOpen(false);
    setCopyOverride(null);
    setVoiceFeedback(null);
    setManualVoiceCaption({
      submitted: false,
      text: ""
    });
    setVoiceDestinationSearchRequest(null);
    setAccidentGuideOpen(false);
    setAccidentCallVisible(true);
    setAudioCompanionDemo(preset.id === "voice-companion" ? createInitialAudioCompanionState() : null);
    setVideoCompanionDemo(preset.id === "video-companion" ? createInitialVideoCompanionState() : null);
  }

  async function handleVoiceTranscript(
    transcript: string,
    semanticOverride: SemanticIntentResponse | null = null
  ) {
    if (handleCompanionTranscript(transcript)) {
      return;
    }

    const voiceContext: VoiceCommandContext = {
      activeServiceId: summary.activeDecision?.scenario.id ?? null,
      topServiceId: summary.topDecision.score > 0 ? summary.topDecision.scenario.id : null,
      state: decisionState
    };
    const localIntent = interpretVoiceCommand(transcript, voiceContext);
    let voiceIntent = localIntent;

    if (semanticOverride) {
      voiceIntent = applySemanticInterpretation(transcript, voiceContext, semanticOverride);
    } else {
      try {
        const semanticIntent = await requestSemanticIntent({
          transcript,
          state: decisionState,
          activeServiceId: voiceContext.activeServiceId,
          topServiceId: voiceContext.topServiceId
        }, modelSettings);
        voiceIntent = applySemanticInterpretation(transcript, voiceContext, semanticIntent);
      } catch {
        voiceIntent = localIntent;
      }
    }

    setVoiceFeedback(voiceIntent);
    voiceOutput.speak(voiceIntent.speechText);

    if (!voiceIntent.matched) {
      return;
    }

    if (voiceIntent.destinationSearch) {
      setVoiceDestinationSearchRequest({
        id: Date.now(),
        serviceId: voiceIntent.focusService
      });
    }

    setState((current) => withDerivedRange({
      ...current,
      ...voiceIntent.statePatch,
      scriptTrigger: "none"
    }));
    setSelectedService(voiceIntent.focusService);
    setConfirmedDecision(null);
    setChargingSession(idleChargingSession);
    setCarwashMode(idleCarwashMode);
    setRefuelAuthorization(idleRefuelAuthorization);
    setParcelExitReminder(idleParcelExitReminder);
    setParkingPaymentOpen(false);
    setCopyOverride(null);
    setAccidentGuideOpen(false);
    setAccidentCallVisible(true);
    setAudioCompanionDemo(null);
    setVideoCompanionDemo(null);
  }

  function handleCompanionTranscript(transcript: string) {
    return handleAudioCompanionTranscript(transcript) || handleVideoCompanionTranscript(transcript);
  }

  function playCompanionVoiceFeedback(
    feedback: VoiceCommandIntent,
    audioAsset?: { audioSrc: string; spokenText: string }
  ) {
    setVoiceFeedback(feedback);

    if (audioAsset?.audioSrc) {
      return voiceOutput.speakAudio(
        assetUrl(audioAsset.audioSrc, { versioned: false }),
        audioAsset.spokenText || feedback.speechText || feedback.message
      );
    }

    return voiceOutput.speak(feedback.speechText || feedback.message);
  }

  function handleAudioCompanionTranscript(transcript: string) {
    if (!audioCompanionDemo) {
      return false;
    }

    const intent = matchAudioCompanionVoiceIntent(transcript);

    if (!intent) {
      return false;
    }

    if (audioCompanionDemo.stage !== "p2_playing") {
      const message = audioCompanionDemo.stage === "p1_recommend"
        ? "先选择一档内容开始播放，我再继续帮你问答。"
        : audioCompanionDemo.stage === "p2_preview"
          ? "先点右侧播放卡展开全屏，我再继续帮你问答。"
        : "当前内容正在处理中，稍等我回到播放页再继续。";
      const feedbackAudio = audioCompanionDemo.stage === "p1_recommend"
        ? getCompanionFeedbackAudio("audioSelectFirst")
        : audioCompanionDemo.stage === "p2_preview"
          ? getCompanionFeedbackAudio("audioExpandPreview")
          : getCompanionFeedbackAudio("audioBusy");
      playCompanionVoiceFeedback(createAudioCompanionVoiceFeedback({
        matched: false,
        message,
        transcript
      }), feedbackAudio);
      return true;
    }

    if (intent === "explain") {
      const message = AUDIO_COMPANION_ANSWER.text;
      setVoiceFeedback(createAudioCompanionVoiceFeedback({
        matched: true,
        message,
        transcript
      }));
      setAudioCompanionDemo((current) => current
        ? {
            ...current,
            stage: "p3_answer"
          }
        : current
      );
      return true;
    }

    if (intent === "similar") {
      setVoiceFeedback(createAudioCompanionVoiceFeedback({
        matched: true,
        message: AUDIO_COMPANION_SIMILAR_REPLY,
        transcript
      }));
      setAudioCompanionDemo((current) => current
        ? {
            ...current,
            stage: "p4_similar"
          }
        : current
      );
      return true;
    }

    handleStartAudioCompanionGeneration(transcript);
    return true;
  }

  function handlePreviewAudioCompanionProgram(programId: AudioCompanionProgramId) {
    setAudioCompanionDemo((current) => current
      ? {
          ...current,
          selectedProgramId: programId,
          stage: "p2_preview"
        }
      : current
    );
  }

  function handleExpandAudioCompanionPreview() {
    setAudioCompanionDemo((current) => current?.stage === "p2_preview"
      ? {
          ...current,
          stage: "p2_playing"
        }
      : current
    );
  }

  function handleSelectAudioCompanionProgram(programId: AudioCompanionProgramId) {
    setAudioCompanionDemo((current) => current
      ? {
          ...current,
          selectedProgramId: programId,
          stage: "p2_playing"
        }
      : current
    );
  }

  function handleBackToAudioCompanionRecommendation() {
    setAudioCompanionDemo((current) => {
      if (current?.stage === "p2_playing") {
        return {
          ...current,
          stage: "p2_preview"
        };
      }

      if (current?.stage === "p2_preview") {
        return {
          ...current,
          stage: "p1_recommend"
        };
      }

      return current;
    });
  }

  function handleAudioCompanionAnswerFinished() {
    setAudioCompanionDemo((current) => current?.stage === "p3_answer"
      ? {
          ...current,
          stage: "p2_playing"
        }
      : current
    );
  }

  function handleStartAudioCompanionGeneration(transcript = "深入分析英伟达财报") {
    const generationRunId = Date.now();

    const generationIntroPlayback = playCompanionVoiceFeedback(createAudioCompanionVoiceFeedback({
      matched: true,
      message: AUDIO_COMPANION_GENERATION_REPLY,
      transcript
    }), getCompanionFeedbackAudio("audioGenerationStart"));
    setAudioCompanionDemo((current) => current
      ? {
          ...current,
          generationIntroFinishedRunId: 0,
          generationRunId,
          stage: "p5_generating"
        }
      : current
    );

    void generationIntroPlayback.finished.then(() => {
      setAudioCompanionDemo((current) => {
        if (current?.stage !== "p5_generating" || current.generationRunId !== generationRunId) {
          return current;
        }

        return {
          ...current,
          generationIntroFinishedRunId: generationRunId
        };
      });
    });
  }

  function handleStartGeneratedAudioCompanionProgram() {
    setAudioCompanionDemo((current) => current
      ? {
          ...current,
          selectedProgramId: "nvidia-generated",
          stage: "p2_playing"
        }
      : current
    );
  }

  function handleVideoCompanionTranscript(transcript: string) {
    if (!videoCompanionDemo) {
      return false;
    }

    const intent = matchVideoCompanionVoiceIntent(transcript);

    if (!intent) {
      return false;
    }

    if (intent === "reset") {
      const message = "已回到视频推荐页。";
      setVideoCompanionVoiceFeedback(transcript, message, true, getCompanionFeedbackAudio("videoReset"));
      setVideoCompanionDemo(createInitialVideoCompanionState());
      return true;
    }

    if (intent === "actor") {
      handleVideoCompanionAskActor(transcript);
      return true;
    }

    if (intent === "continue") {
      handleVideoCompanionPlaybackEnded(transcript);
      return true;
    }

    handleVideoCompanionOpenQuicklook(transcript);
    return true;
  }

  function setVideoCompanionVoiceFeedback(
    transcript: string,
    message: string,
    matched = true,
    audioAsset?: { audioSrc: string; spokenText: string }
  ) {
    playCompanionVoiceFeedback(createCompanionVoiceFeedback({
      companionName: "视频搭子",
      focusService: "charging",
      transcript,
      matched,
      message
    }), audioAsset);
  }

  function handleVideoCompanionSelectVideo(
    videoId: VideoCompanionVideoId,
    nextStage: VideoCompanionDemoState["stage"] = "p2_playing"
  ) {
    setVideoCompanionDemo((current) => current
      ? {
          ...current,
          selectedVideoId: videoId,
          stage: nextStage
        }
      : current
    );
  }

  function handleVideoCompanionAskActor(transcript = "这人是谁") {
    const currentVideo = videoCompanionDemo ? getVideoCompanionVideo(videoCompanionDemo.selectedVideoId) : null;
    const person = currentVideo?.person;
    const message = person
      ? `${person.name}。${person.relation}。${person.summary}`
      : "已经为你找到当前画面相关人物。";

    setVideoCompanionVoiceFeedback(
      transcript,
      message,
      true,
      currentVideo ? getVideoActorFeedbackAudio(currentVideo.id) : getCompanionFeedbackAudio("videoActorFallback")
    );
    setVideoCompanionDemo((current) => current
      ? {
          ...current,
          stage: "p5_actor"
        }
      : current
    );
  }

  function handleVideoCompanionOpenQuicklook(transcript = "帮我找速看版") {
    const message = "正在帮你找刚出炉的速看版。";
    setVideoCompanionVoiceFeedback(transcript, message, true, getCompanionFeedbackAudio("videoQuicklook"));
    setVideoCompanionDemo((current) => current
      ? {
          ...current,
          stage: "p4_quicklook"
        }
      : current
    );
  }

  function handleVideoCompanionPlaybackEnded(transcript = "片尾") {
    const message = "还有时间，要继续看点别的吗？我为你推荐了相关内容。";
    setVideoCompanionVoiceFeedback(transcript, message, true, getCompanionFeedbackAudio("videoContinue"));
    setVideoCompanionDemo((current) => current
      ? {
          ...current,
          stage: "p3_continue"
        }
      : current
    );
  }

  function handleVideoCompanionActorFinished() {
    setVideoCompanionDemo((current) => current?.stage === "p5_actor"
      ? {
          ...current,
          stage: "p2_playing"
        }
      : current
    );
  }

  function handleVideoCompanionReset() {
    setVideoCompanionDemo(createInitialVideoCompanionState());
  }

  function handleParcelArriveHome({
    pendingCount,
    pickupPointName
  }: {
    pendingCount: number;
    pickupPointName: string;
  }) {
    setParcelExitReminder(createParcelExitReminder({
      pendingCount,
      pickupPointName
    }));
    setSelectedService(null);
    setConfirmedDecision(null);
    setChargingSession(idleChargingSession);
    setCarwashMode(idleCarwashMode);
    setRefuelAuthorization(idleRefuelAuthorization);
    setParkingPaymentOpen(false);
    setCopyOverride(null);
    setAccidentGuideOpen(false);
    setAccidentCallVisible(true);
    setState((current) => withDerivedRange({
      ...current,
      desktop: "ai",
      location: "home",
      parked: true,
      parcelPending: true,
      parcelOnRoute: true
    }));
  }

  function handleRefuelArriveStation(station: FuelStation) {
    setRefuelAuthorization(createRefuelAuthorizationPrompt(station));
    setSelectedService(null);
    setConfirmedDecision(null);
    setChargingSession(idleChargingSession);
    setCarwashMode(idleCarwashMode);
    setParcelExitReminder(idleParcelExitReminder);
    setParkingPaymentOpen(false);
    setCopyOverride(null);
    setAccidentGuideOpen(false);
    setAccidentCallVisible(true);
    setState((current) => withDerivedRange({
      ...current,
      desktop: "ai",
      fuelLevel: Math.min(current.fuelLevel, 28),
      fuelRangeKm: Math.min(current.fuelRangeKm, 220),
      location: "destination",
      parked: true
    }));
  }

  function handleCarwashArriveShop(shop: CarwashShop) {
    setCarwashMode(createCarwashModePrompt(shop));
    setSelectedService(null);
    setConfirmedDecision(null);
    setChargingSession(idleChargingSession);
    setParcelExitReminder(idleParcelExitReminder);
    setRefuelAuthorization(idleRefuelAuthorization);
    setParkingPaymentOpen(false);
    setCopyOverride(null);
    setAccidentGuideOpen(false);
    setAccidentCallVisible(true);
    setState((current) => withDerivedRange({
      ...current,
      carwashCoupon: true,
      desktop: "ai",
      location: "destination",
      parked: true
    }));
  }

  function handleParkingPaymentPaid() {
    const parkingDecision = summary.decisions.find((decision) => decision.scenario.id === "parking") ?? null;
    setParkingPaymentOpen(false);
    setSelectedService(null);
    setConfirmedDecision(parkingDecision);
    setTimeout(() => setConfirmedDecision(null), 4200);
  }

  function handleCloseService() {
    setSelectedService(null);
    setParkingPaymentOpen(false);
  }

  function handleConfirmAccidentGuide() {
    setAccidentGuideOpen(false);
    setAccidentCallVisible(true);
  }

  function handleStartChargingSession(station: ChargingStation) {
    setChargingSession(createChargingSession({
      batteryLevel: decisionState.batteryLevel,
      stationName: station.shortName || station.name
    }));
    setCarwashMode(idleCarwashMode);
    setRefuelAuthorization(idleRefuelAuthorization);
    setSelectedService(null);
    setConfirmedDecision(null);
    setParkingPaymentOpen(false);
    setCopyOverride(null);
    setAccidentGuideOpen(false);
    setAccidentCallVisible(true);
    setState((current) => withDerivedRange({
      ...current,
      desktop: "ai",
      location: "destination",
      parked: true
    }));
  }

  function handleConfirmChargingCover() {
    setChargingSession((current) => current.stage === "coverPrompt"
      ? {
          ...current,
          chargingPortOpen: true,
          isCharging: true,
          stage: "chargingStarted"
        }
      : current
    );
  }

  function handleEnableChargingRestMode() {
    setChargingSession((current) => current.stage === "idle"
      ? current
      : {
          ...current,
          chargingPortOpen: true,
          isCharging: true,
          restModeEnabled: true,
          stage: "restActive"
        }
    );
  }

  function handleShowChargingRestRecommendation() {
    setChargingSession((current) => current.stage === "idle"
      ? current
      : {
          ...current,
          chargingPortOpen: true,
          isCharging: true,
          stage: "restRecommend"
        }
    );
  }

  function handleDismissChargingSession() {
    setChargingSession(idleChargingSession);
  }

  function handleConfirmCarwashMode() {
    setCarwashMode((current) => activateCarwashMode(current));
  }

  function handleDismissCarwashMode() {
    setCarwashMode(idleCarwashMode);
  }

  function handleAuthorizeRefuel() {
    setRefuelAuthorization((current) => authorizeRefuel(current));
  }

  function handleDismissRefuelAuthorization() {
    setRefuelAuthorization(idleRefuelAuthorization);
  }

  return (
    <CockpitDataProvider value={cockpitData}>
      <div className="app" style={themeToCssVars(cockpitTheme)}>
        <AmbientBackdrop state={decisionState} />
        <div className="demo-layout">
          <div className="demo-main">
            <section className="cockpit-frame" aria-label="车机界面">
              <DesktopStage
                state={decisionState}
                summary={summary}
                selectedService={selectedService}
                voiceDestinationSearchRequest={voiceDestinationSearchRequest}
                voiceCaption={{
                  active: voiceCaptionActive,
                  phase: realtimeVoice.phase,
                  text: voiceCaptionText
                }}
                audioCompanionDemo={audioCompanionDemo}
                videoCompanionDemo={videoCompanionDemo}
                carwashMode={carwashMode}
                chargingSession={chargingSession}
                parcelExitReminder={parcelExitReminder}
                parkingPaymentOpen={parkingPaymentOpen}
                refuelAuthorization={refuelAuthorization}
                accidentGuideOpen={accidentGuideOpen}
                accidentCallVisible={accidentCallVisible}
                onOpenService={setSelectedService}
                onCloseService={handleCloseService}
                onConfirmAccidentGuide={handleConfirmAccidentGuide}
                onHangupAccidentCall={() => setAccidentCallVisible(false)}
                onConfirmService={handleConfirm}
                onCarwashArriveShop={handleCarwashArriveShop}
                onConfirmCarwashMode={handleConfirmCarwashMode}
                onConfirmChargingCover={handleConfirmChargingCover}
                onDismissCarwashMode={handleDismissCarwashMode}
                onDismissChargingSession={handleDismissChargingSession}
                onDismissParcelExitReminder={() => setParcelExitReminder(idleParcelExitReminder)}
                onDismissRefuelAuthorization={handleDismissRefuelAuthorization}
                onEnableChargingRestMode={handleEnableChargingRestMode}
                onParcelArriveHome={handleParcelArriveHome}
                onRefuelArriveStation={handleRefuelArriveStation}
                onAuthorizeRefuel={handleAuthorizeRefuel}
                onAudioCompanionAnswerFinished={handleAudioCompanionAnswerFinished}
                onAudioCompanionBackToRecommend={handleBackToAudioCompanionRecommendation}
                onAudioCompanionFallbackSpeak={voiceOutput.speak}
                onAudioCompanionExpandPreview={handleExpandAudioCompanionPreview}
                onAudioCompanionPreviewProgram={handlePreviewAudioCompanionProgram}
                onAudioCompanionSelectProgram={handleSelectAudioCompanionProgram}
                onAudioCompanionStartGeneratedProgram={handleStartGeneratedAudioCompanionProgram}
                onVideoCompanionActorFinished={handleVideoCompanionActorFinished}
                onVideoCompanionAskActor={handleVideoCompanionAskActor}
                onVideoCompanionOpenQuicklook={handleVideoCompanionOpenQuicklook}
                onVideoCompanionPlaybackEnded={handleVideoCompanionPlaybackEnded}
                onVideoCompanionReset={handleVideoCompanionReset}
                onVideoCompanionSelectVideo={handleVideoCompanionSelectVideo}
                onShowChargingRestRecommendation={handleShowChargingRestRecommendation}
                onStartChargingSession={handleStartChargingSession}
                onCloseParkingPayment={() => setParkingPaymentOpen(false)}
                onParkingPaymentPaid={handleParkingPaymentPaid}
              />
              {confirmedDecision ? (
                <div className="success-toast">
                  <CheckCircle2 size={19} />
                  <span>{confirmedDecision.scenario.successMessage}</span>
                </div>
              ) : null}
            </section>
          </div>

          <ControlDrawer
            open
            permanent
            state={state}
            onChange={patchState}
            onClose={() => undefined}
            onPreset={handlePreset}
            onReset={handleReset}
            onSettings={() => setSettingsOpen(true)}
            onRealtimeVoiceStart={realtimeVoice.startAudioSession}
            onRealtimeVoiceStop={realtimeVoice.stopAudioSession}
            onTtsEnabledChange={voiceOutput.setEnabled}
            onTtsStop={voiceOutput.stop}
            onManualVoiceCaptionChange={handleManualVoiceCaptionChange}
            onManualVoiceCaptionSubmit={handleManualVoiceCaptionSubmit}
            onVoiceTranscript={realtimeVoice.runTranscript}
            realtimeVoiceAudioListening={realtimeVoice.audioListening}
            realtimeVoiceAudioSupported={realtimeVoice.audioSupported}
            realtimeVoiceConnected={realtimeVoice.connected}
            realtimeVoiceError={realtimeVoice.error}
            realtimeVoicePhase={realtimeVoice.phase}
            realtimeVoiceProvider={realtimeVoice.provider}
            realtimeVoiceReplyText={realtimeVoice.replyText}
            realtimeVoiceTranscript={realtimeVoice.transcript}
            ttsEnabled={voiceOutput.enabled}
            ttsSpeaking={voiceOutput.speaking}
            ttsSupported={voiceOutput.supported}
            voiceFeedback={voiceFeedback}
          />
        </div>
        <SettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onSaved={setModelSettings}
        />
      </div>
    </CockpitDataProvider>
  );
}

function isTextEntryTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return false;
  }

  const editable = target.closest("input, textarea, select, [contenteditable='true'], [contenteditable='']");

  if (!editable) {
    return false;
  }

  if (editable instanceof HTMLInputElement) {
    const inputType = editable.type.toLowerCase();
    return !["button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit"].includes(inputType);
  }

  return true;
}
